import { useEffect, useState, useCallback } from "react";
import { X, Trash2, Download, Share2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { listPhotos, deletePhoto, clearAll } from "@/lib/idb";
import { t, getLocale } from "@/lib/i18n";

export default function Gallery({ onClose }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await listPhotos();
      const withUrls = items.map((p) => ({
        ...p,
        url: URL.createObjectURL(p.blob),
      }));
      setPhotos(withUrls);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let urls = [];
    listPhotos()
      .then((items) => {
        urls = items.map((p) => URL.createObjectURL(p.blob));
        setPhotos(items.map((p, i) => ({ ...p, url: urls[i] })));
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  const handleDelete = async (id) => {
    await deletePhoto(id);
    setSelected(null);
    await load();
    toast.success(t("photoDeleted"));
  };

  const handleClear = async () => {
    if (!photos.length) return;
    if (!window.confirm(t("deleteAllConfirm", photos.length))) return;
    await clearAll();
    setSelected(null);
    await load();
    toast.success(t("galleryCleared"));
  };

  const handleDownload = (photo) => {
    const link = document.createElement("a");
    const ts = new Date(photo.createdAt).toISOString().replace(/[:.]/g, "-");
    link.download = `real-selfie-${ts}.jpg`;
    link.href = photo.url;
    link.click();
    toast.success(t("photoSaved"));
  };

  const handleShare = async (photo) => {
    try {
      const file = new File([photo.blob], `selfie-${photo.id}.jpg`, {
        type: "image/jpeg",
      });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Real Selfie",
          text: "Moja reálna selfie fotka",
        });
      } else {
        handleDownload(photo);
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error(t("shareFail"));
      }
    }
  };

  return (
    <div
      data-testid="gallery-screen"
      className="fixed inset-0 z-[80] bg-black overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 border-b border-white/5">
        <div>
          <h2 className="font-display text-2xl font-light text-white tracking-tight">
            {t("gallery")}
          </h2>
          <p className="font-mono-label text-white/50 mt-1">
            {t("photos", photos.length)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {photos.length > 0 && (
            <button
              data-testid="gallery-clear-all"
              onClick={handleClear}
              aria-label={t("deleteAll")}
              className="h-11 px-4 rounded-full bg-white/5 border border-white/10 flex items-center gap-2 active:scale-95 transition-transform"
            >
              <Trash2 className="h-4 w-4 text-white/80" strokeWidth={1.8} />
              <span className="font-mono-label text-white/80">{t("deleteAll")}</span>
            </button>
          )}
          <button
            data-testid="close-gallery-button"
            onClick={onClose}
            aria-label={t("gallery")}
            className="h-11 w-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
          >
            <X className="h-5 w-5 text-white" strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <p className="font-mono-label text-white/40">{t("loading")}</p>
          </div>
        ) : photos.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <ImageOff className="h-12 w-12 text-white/30 mb-5" strokeWidth={1.2} />
            <h3 className="font-display text-xl font-light text-white mb-2">
              {t("noPhotos")}
            </h3>
            <p className="text-sm text-white/50 max-w-xs">
              {t("noPhotosHint")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1">
            {photos.map((p) => (
              <button
                key={p.id}
                data-testid={`gallery-thumb-${p.id}`}
                onClick={() => setSelected(p)}
                className="aspect-square rounded-md overflow-hidden bg-white/5 active:opacity-70 transition-opacity"
              >
                <img
                  src={p.url}
                  alt={`Selfie ${p.id}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail viewer */}
      {selected && (
        <div
          data-testid="gallery-detail"
          className="absolute inset-0 z-[90] bg-black flex flex-col"
        >
          <div className="flex items-center justify-between p-6 pt-8">
            <span className="font-mono-label text-white/70">
              {new Date(selected.createdAt).toLocaleString(getLocale())}
            </span>
            <button
              data-testid="close-detail-button"
              onClick={() => setSelected(null)}
              aria-label="Close"
              className="h-11 w-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
            >
              <X className="h-5 w-5 text-white" strokeWidth={1.8} />
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center p-4">
            <img
              src={selected.url}
              alt={`Detail ${selected.id}`}
              className="max-h-full max-w-full object-contain rounded-2xl"
            />
          </div>

          <div className="p-6 pb-10 flex items-center justify-around bg-gradient-to-t from-black/90 to-transparent">
            <button
              data-testid="detail-delete-button"
              onClick={() => handleDelete(selected.id)}
              className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
            >
              <div className="h-12 w-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-white" strokeWidth={1.8} />
              </div>
              <span className="font-mono-label text-white/60">{t("delete")}</span>
            </button>

            <button
              data-testid="detail-share-button"
              onClick={() => handleShare(selected)}
              className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
            >
              <div className="h-14 w-14 rounded-full bg-white flex items-center justify-center">
                <Share2 className="h-5 w-5 text-black" strokeWidth={2} />
              </div>
              <span className="font-mono-label text-white">{t("share")}</span>
            </button>

            <button
              data-testid="detail-download-button"
              onClick={() => handleDownload(selected)}
              className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
            >
              <div className="h-12 w-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <Download className="h-5 w-5 text-white" strokeWidth={1.8} />
              </div>
              <span className="font-mono-label text-white/60">{t("download")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
